import { create } from "zustand";
import {
  calculateReportAfterRejectApproval,
  calculateReportAfterUpdateMasterFalreInfo,
  calculateReportAfterUpdateMasterFlareCompound,
  calculateReportWhenUpdateOrCreate,
  getAllFilterReportGovernment,
  getFilterReportFlareVolume,
  getFilterReportVocEmission,
  getReportGovernment,
  searchReportFlareVolume,
  searchReportVocEmission,
} from "src/services/flare.service";
import { ReportFlareVolumeStoreProps } from "./interface/report-flare-store.interface";
import {
  CalculateReportAfterRejectApproval,
  CalculateReportAfterUpdateMasterdataBody,
  FilterReportFlareVolume,
} from "src/entity/flare/report-flare-volume.entity";
import { FilterReportVocEmission } from "src/entity/flare/report-voc-emission.entity";

const getInitialFilter = () => ({
  sourceName: [],
  gasName: [],
  area: [],
  flareName: [],
});

export const reportFlareStore = create<ReportFlareVolumeStoreProps>((set) => ({
  loadingAll: false,
  loadingGet: false,
  dataReport: {
    total: 0,
    data: [],
  },
  error: null,
  loadingGetVoc: false,
  dataReportVoc: {
    total: 0,
    data: [],
    summary: [],
  },
  erroVoc: null,
  loadingGetMaster: false,
  filterReportFlareVolume: {
    sourceName: [],
    gasName: [],
    area: [],
    flareName: [],
  },
  filterReportVocEmission: {
    sourceName: [],
    gasName: [],
    area: [],
    flareName: [],
  },
  dataCalculateReport: null,
  loadingCalculateReport: false, // use when update [Setup Flare Volume]
  dataCalculateReportAfterChangeMaster: null,
  loadingCalculateReportAfterChangeMaster: false, // use when update [Setup Heating Value, Setting Flare Info]
  loadingCalculateReportWhenRejectApproval: false, // use when Reject Approval
  loadingReportGovernment: false,
  loadingFilterReportGovernment: false,
  filterReportGovernment: {
    monthInYear: [],
    flareName: [],
  },
  dataReportGovernment: null,
  clearError: (type: string) => {
    set(() => ({ [`${type}`]: null }));
  },
  setLoadingAll: (loading: boolean) => {
    set(() => ({ loadingAll: loading }));
  },
  searchReportFlareVolume: async (filter: FilterReportFlareVolume) => {
    set(() => ({ loadingGet: true }));
    try {
      const res = await searchReportFlareVolume(filter);
      set(() => ({ loadingGet: false, dataReport: res.data?.data }));
    } catch (err: any) {
      set(() => ({ loadingGet: false, error: err?.message, dataReport: { data: [], total: 0, summary: [] } }));
    }
  },
  searchReportVocEmission: async (filter: FilterReportVocEmission) => {
    set(() => ({ loadingGetVoc: true }));
    try {
      const res = await searchReportVocEmission(filter);
      set(() => ({ loadingGetVoc: false, dataReportVoc: res.data?.data }));
    } catch (err: any) {
      set(() => ({ loadingGetVoc: false, error: err?.message, dataReportVoc: { data: [], total: 0, summary: [] } }));
    }
  },
  getFilterColumnReportVocEmission: async (year: string) => {
    set(() => ({ loadingGetMaster: true }));
    try {
      const res = await getFilterReportVocEmission({ year });
      set(() => ({
        loadingGetMaster: false,
        filterReportVocEmission: res.data?.data,
      }));
    } catch (err: any) {
      set(() => ({
        loadingGetMaster: false,
        filterReportVocEmission: getInitialFilter(),
      }));
    }
  },
  getFilterColumnReportFlareVolume: async (year: string) => {
    set(() => ({ loadingGetMaster: true }));
    try {
      const res = await getFilterReportFlareVolume({ year });
      set(() => ({
        loadingGetMaster: false,
        filterReportFlareVolume: res.data?.data,
      }));
    } catch (err: any) {
      set(() => ({
        loadingGetMaster: false,
        filterReportFlareVolume: getInitialFilter(),
      }));
    }
  },
  calculateReportFlareVolume: async (flareId: string) => {
    set(() => ({ loadingCalculateReport: true }));
    try {
      const res = await calculateReportWhenUpdateOrCreate(flareId);
      set(() => ({
        loadingCalculateReport: false,
        dataCalculateReport: res.data?.data,
      }));
    } catch (err: any) {
      set(() => ({
        loadingCalculateReport: false,
        dataCalculateReport: null,
      }));
    }
  },
  getAllFilterReportGovernment: async () => {
    set(() => ({ loadingFilterReportGovernment: true }));
    try {
      const res = await getAllFilterReportGovernment();
      set(() => ({
        loadingFilterReportGovernment: false,
        filterReportGovernment: res.data?.data,
      }));

      return res.data?.data || null;
    } catch (err: any) {
      set(() => ({
        loadingFilterReportGovernment: false,
        filterReportGovernment: {
          monthInYear: [],
          flareName: [],
        },
      }));
    }
  },
  getReportGovernment: async (year: string | null, month: string | null, flareName: string | null) => {
    set(() => ({ loadingReportGovernment: true }));
    try {
      const res = await getReportGovernment({ year, month, flareName });
      set(() => ({
        loadingReportGovernment: false,
        dataReportGovernment: res.data?.data,
      }));
    } catch (err: any) {
      set(() => ({
        loadingReportGovernment: false,
        dataReportGovernment: null,
      }));
    }
  },
  calculateReportAfterUpdateMasterFalreInfo: async (body: CalculateReportAfterUpdateMasterdataBody) => {
    set(() => ({ loadingCalculateReportAfterChangeMaster: true }));
    try {
      const res = await calculateReportAfterUpdateMasterFalreInfo(body);
      set(() => ({
        loadingCalculateReportAfterChangeMaster: false,
        dataCalculateReportAfterChangeMaster: res.data?.data,
      }));
    } catch (err: any) {
      set(() => ({
        loadingCalculateReportAfterChangeMaster: false,
        dataCalculateReportAfterChangeMaster: null,
      }));
    }
  },
  calculateReportAfterRejectApproval: async (body: CalculateReportAfterRejectApproval) => {
    set(() => ({ loadingCalculateReportWhenRejectApproval: true }));
    try {
      await calculateReportAfterRejectApproval(body);
      set(() => ({
        loadingCalculateReportWhenRejectApproval: false,
      }));
    } catch (err: any) {
      set(() => ({
        loadingCalculateReportWhenRejectApproval: false,
      }));
    }
  },
  calculateReportAfterUpdateMasterFlareCompound: async (_id: string) => {
    set(() => ({ loadingCalculateReportAfterChangeMaster: true }));
    try {
      await calculateReportAfterUpdateMasterFlareCompound(_id);
      set(() => ({
        loadingCalculateReportAfterChangeMaster: false,
      }));
    } catch (err: any) {
      set(() => ({
        loadingCalculateReportAfterChangeMaster: false,
      }));
    }
  },
}));
